import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { costoService } from '@/services/costoService';
import { PRESUPUESTOS_PROFORMA_QUERY_KEY } from '@/presupuestos/hooks/usePresupuestosProforma';
import { KANBAN_QUERY_KEY } from '@/components/TramitesKanban/kanbanUtils';
import { aplicarOrden } from '@/presupuestos/utils/orden';
import type { CostoFormData, CostosResponse, TotalesPorParte } from '@/types/costo';

const TOTALES_INICIALES = {
  ars: { ingresos: 0, egresos: 0, balance: 0 },
  usd: { ingresos: 0, egresos: 0, balance: 0 },
};

const TOTALES_POR_PARTE_INICIALES: TotalesPorParte = {
  adquiriente: { ARS: 0, USD: 0 },
  transferente: { ARS: 0, USD: 0 },
};

/**
 * Hook principal para gestionar items de presupuesto
 */
export const useCostos = (tramiteId: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['costos', tramiteId];

  // Los cambios de la escritura se propagan al presupuesto importado: se
  // invalida también su caché para verlos al entrar por Presupuestos.
  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey });
    queryClient.invalidateQueries({ queryKey: PRESUPUESTOS_PROFORMA_QUERY_KEY });
  };

  // Query: Get items
  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => costoService.getCostos(tramiteId),
    enabled: !!tramiteId,
    staleTime: 0,
  });

  // Mutation: Create
  const crearMutation = useMutation({
    mutationFn: (formData: CostoFormData) =>
      costoService.crearCosto(tramiteId, formData),
    onSuccess: invalidar,
  });

  // Mutation: Update
  const actualizarMutation = useMutation({
    mutationFn: ({
      costoId,
      data,
    }: {
      costoId: string;
      data: Partial<CostoFormData>;
    }) => costoService.actualizarCosto(tramiteId, costoId, data),
    onSuccess: invalidar,
  });

  // Mutation: Delete
  const eliminarMutation = useMutation({
    mutationFn: (costoId: string) =>
      costoService.eliminarCosto(tramiteId, costoId),
    onSuccess: invalidar,
  });

  // Mutation: Reorder (orden de impresión de una caja)
  const reordenarMutation = useMutation({
    mutationFn: (ids: string[]) => costoService.reordenarCostos(tramiteId, ids),
    // El orden se propaga al presupuesto importado; si falló, se recarga el del servidor.
    onSettled: invalidar,
  });

  /**
   * Se aplica en la caché antes de la request para que la lista no vuelva un
   * instante al orden anterior.
   */
  const reordenarCostos = (ids: string[]) => {
    queryClient.setQueryData<CostosResponse | undefined>(queryKey, (actual) =>
      actual ? { ...actual, costos: aplicarOrden(actual.costos, ids) } : actual
    );

    return reordenarMutation.mutateAsync(ids);
  };

  // Mutation: Copy from/to another tramite
  const copiarMutation = useMutation({
    mutationFn: ({
      targetTramiteId,
      sourceTramiteId,
    }: {
      targetTramiteId: string;
      sourceTramiteId: string;
    }) => costoService.copiarPresupuesto(targetTramiteId, sourceTramiteId),
    onSuccess: (_data, { targetTramiteId }) => {
      queryClient.invalidateQueries({ queryKey: ['costos', targetTramiteId] });
      queryClient.invalidateQueries({ queryKey: PRESUPUESTOS_PROFORMA_QUERY_KEY });
    },
  });

  // Mutation: Import a standalone budget (linked copy)
  const importarMutation = useMutation({
    mutationFn: ({
      presupuestoProformaId,
      reemplazar,
    }: {
      presupuestoProformaId: string;
      reemplazar?: boolean;
    }) => costoService.importarProforma(tramiteId, presupuestoProformaId, reemplazar),
    onSuccess: () => {
      invalidar();
      // El indicador de presupuesto de la escritura depende del vínculo.
      queryClient.invalidateQueries({ queryKey: KANBAN_QUERY_KEY });
    },
  });

  return {
    // Data
    costos: data?.costos || [],
    totales: data?.totales || TOTALES_INICIALES,
    totalesPorParte: data?.totalesPorParte || TOTALES_POR_PARTE_INICIALES,
    presupuestoVinculado: data?.presupuestoVinculado ?? null,
    isLoading,
    error,

    // Mutations
    crearCosto: crearMutation.mutateAsync,
    actualizarCosto: actualizarMutation.mutateAsync,
    eliminarCosto: eliminarMutation.mutateAsync,
    copiarPresupuesto: copiarMutation.mutateAsync,
    importarProforma: importarMutation.mutateAsync,
    reordenarCostos,

    // Loading states
    isCreando: crearMutation.isPending,
    isActualizando: actualizarMutation.isPending,
    isEliminando: eliminarMutation.isPending,
    isCopiando: copiarMutation.isPending,
    isImportando: importarMutation.isPending,
  };
};
